import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import festivalDeViajes from 'images/icons/festivaldeviajes.svg';
import lettersHotSale from 'images/icons/lettersHotSale.svg';
import confettiIcon from 'images/icons/confetti.png';
import { Trans, useTranslation } from 'react-i18next';
import { Spacing, Text } from '@reservamos/elements';
import ModalWithTheme from 'components/ModalWithTheme';
import getSearchAnalyticsData from 'utils/search/getSearchAnalyticsData';
import { trackSpecialDiscountBannerShown } from 'metrics/user-analytics/search';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';

// Dedupe impressions per (variant, searchId) within a session: the banner is mounted twice per
// render (mobile slot + desktop aside slot) and we want a single Mixpanel event per impression;
// a new search produces a new searchId and re-fires.
const trackedBannerImpressions = new Set();

const PROVIDER_ANNIVERSARY_DISCOUNT_PERCENT = 'PROVIDER_ANNIVERSARY_DISCOUNT_PERCENT';

const SINGLE_DISCOUNT_DEFAULTS = {
  showPercent: true,
  showDiscountLabel: true,
  secondaryTextKey: 'online_only',
  secondaryTextClassName: 'text-white text-[0.5rem]',
};

const VARIANT_CONFIG = {
  etn: { badgeSrc: lettersHotSale, discountType: 'roundTripDiscount', hotSaleBadge: true },
  gho: { badgeSrc: festivalDeViajes, discountType: 'roundTripDiscount', hotSaleBadge: false },
  costa: {
    badgeSrc: festivalDeViajes,
    discountType: 'single',
    discountPercent: 40,
    hotSaleBadge: false,
  },
  gfa: {
    badgeSrc: lettersHotSale,
    discountType: 'single',
    hotSaleBadge: true,
    singleDiscount: {
      showPercent: false,
      showDiscountLabel: false,
      secondaryTextKey: 'subject_to_promo_availability',
      secondaryTextClassName: 'text-white text-xs md:text-sm',
    },
  },
  rochoa: {
    discountPercent: PROVIDER_ANNIVERSARY_DISCOUNT_PERCENT,
  },
};

const TAG_CLIP_PATH = 'polygon(4% 0%, 0% 50%, 4% 100%, 100% 100%, 100% 0%)';

const ROCHOA_TERMS_PARAGRAPH_KEYS = [
  'special_discount_banner.rochoa.terms_modal.paragraph_1',
  'special_discount_banner.rochoa.terms_modal.paragraph_2',
  'special_discount_banner.rochoa.terms_modal.paragraph_3',
  'special_discount_banner.rochoa.terms_modal.paragraph_4',
  'special_discount_banner.rochoa.terms_modal.paragraph_5',
  'special_discount_banner.rochoa.terms_modal.paragraph_6',
  'special_discount_banner.rochoa.terms_modal.paragraph_7',
];

const ROCHOA_TERMS_SEAT_KEYS = [
  'special_discount_banner.rochoa.terms_modal.seat_rey_dorado',
  'special_discount_banner.rochoa.terms_modal.seat_de_mi_tierra',
  'special_discount_banner.rochoa.terms_modal.seat_lo_nuestro',
];

/**
 * Rochoa anniversary promotional banner.
 * The banner message opens a terms and conditions modal on click.
 * @param {object} props - anniversaryPercent.
 * @param {number} props.anniversaryPercent - Discount percentage shown in the banner message.
 * @returns {JSX.Element} The rendered banner.
 */
const RochoaAnniversaryBanner = ({ anniversaryPercent }) => {
  const { t } = useTranslation('search');

  return (
    <div className="drop-shadow-lg w-full md:w-auto">
      <div
        className="bg-white inline-flex items-center gap-3 py-2 pl-5 pr-4 rounded-lg relative w-full overflow-hidden"
        style={{
          clipPath: TAG_CLIP_PATH,
        }}
      >
        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-white rounded-full shadow-inner border border-solid border-gray-200 z-10" />

        <img
          src={confettiIcon}
          alt=""
          aria-hidden="true"
          className="h-8 w-8 object-contain flex-shrink-0 relative z-10"
        />

        <ModalWithTheme
          title={t('special_discount_banner.rochoa.terms_modal.title')}
          responsiveSize="L"
          contentHeight="auto"
          closeOnOverlayClick
          modalTrigger={
            <p className="text-sm text-black relative z-10 cursor-pointer">
              <Trans
                t={t}
                i18nKey="special_discount_banner.rochoa.general_message"
                values={{ percent: anniversaryPercent }}
                components={{ b: <span className="font-bold" /> }}
              />
            </p>
          }
        >
          <Spacing vertical size="S">
            {ROCHOA_TERMS_PARAGRAPH_KEYS.map((key) => (
              <Text key={key} size="S">
                {t(key, { percent: anniversaryPercent })}
              </Text>
            ))}
            <Text size="S" weight="bold">
              {t('special_discount_banner.rochoa.terms_modal.seats_title')}
            </Text>
            {ROCHOA_TERMS_SEAT_KEYS.map((key) => (
              <Text key={key} size="S">
                {t(key)}
              </Text>
            ))}
          </Spacing>
        </ModalWithTheme>
      </div>
    </div>
  );
};

RochoaAnniversaryBanner.propTypes = {
  anniversaryPercent: PropTypes.number.isRequired,
};

/**
 * SpecialDiscountBanner component.
 * Render a promotional banner with a tag shape, gradient background, and discount highlight block.
 * @param {object} props - variant.
 * @param {'etn'|'gho'|'costa'|'gfa'|'rochoa'} props.variant - Brand variant that determines badge icon and discount layout.
 * @returns {JSX.Element} The rendered component.
 */
const SpecialDiscountBanner = ({ variant }) => {
  const variantConfig = VARIANT_CONFIG[variant] || VARIANT_CONFIG.gho;
  const { badgeSrc, discountType, discountPercent, hotSaleBadge } = variantConfig;
  const singleDiscount = { ...SINGLE_DISCOUNT_DEFAULTS, ...variantConfig.singleDiscount };
  const isHotSaleBadge = hotSaleBadge;
  const isCosta = variant === 'costa';
  const isRochoa = variant === 'rochoa';
  const features = useWhitelabelFeatures();
  const anniversaryPercent =
    typeof discountPercent === 'string' ? features?.[discountPercent] : discountPercent;
  const { t } = useTranslation('search');

  const search = useSelector((state) => state.search);
  const trips = useSelector((state) => state.trips);
  const cities = useSelector((state) => state.cities);
  const terminals = useSelector((state) => state.terminals);

  const analyticSearchData = useMemo(() => {
    if (!search) return null;
    const departureId = search.getIn(['departure', 'id']);
    const returnId = search.getIn(['return', 'id']);
    const places = { ...cities, ...terminals };
    return getSearchAnalyticsData({
      search,
      places,
      departures: departureId ? trips?.get(departureId) : null,
      returns: returnId ? trips?.get(returnId) : null,
    }).search;
  }, [search, trips, cities, terminals]);

  useEffect(() => {
    if (!analyticSearchData) return;
    const dedupeKey = `${variant}|${analyticSearchData.id ?? ''}`;
    if (trackedBannerImpressions.has(dedupeKey)) return;
    trackedBannerImpressions.add(dedupeKey);
    trackSpecialDiscountBannerShown(analyticSearchData, { variant });
  }, [variant, analyticSearchData]);

  return (
    <div className="flex flex-col gap-2 max-w-xl w-full md:w-auto">
      {isRochoa && <RochoaAnniversaryBanner anniversaryPercent={anniversaryPercent} />}

      {!isRochoa && (
        <div className="drop-shadow-lg w-full">
          <div
            className="flex items-center gap-3 pl-5 rounded-lg relative overflow-hidden justify-between"
            style={{
              clipPath: TAG_CLIP_PATH,
              background: 'linear-gradient(to right, #d32f2f, #8b1a1a)',
            }}
          >
            <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full" />

            <img
              src={badgeSrc}
              alt=""
              aria-hidden="true"
              className={`h-auto w-8 object-contain ml-2 ${isHotSaleBadge ? 'md:w-10' : 'md:w-20'}`}
            />

            <p className="text-white text-xs md:text-lg font-bold whitespace-nowrap">
              {t('special_discount_banner.title')}
            </p>

            <div className="flex flex-col items-end">
              {discountType === 'single' ? (
                <div
                  className={`flex items-center gap-1 rounded-md px-2 py-1 ${
                    isCosta ? 'bg-blue-600' : ''
                  }`}
                >
                  {singleDiscount.showPercent && (
                    <div className="flex flex-col items-start">
                      <span className="text-white text-xs font-bold">
                        {t('special_discount_banner.up_to')}
                      </span>
                      <span className="text-white text-lg md:text-2xl font-bold leading-none">
                        {discountPercent}%
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col items-start">
                    {singleDiscount.showDiscountLabel && (
                      <span
                        className={`text-xs font-bold rounded px-1 ${
                          isCosta ? 'bg-yellow-400 text-black' : 'text-white'
                        }`}
                      >
                        {t('special_discount_banner.discount_label')}
                      </span>
                    )}
                    <span className={singleDiscount.secondaryTextClassName}>
                      {t(`special_discount_banner.${singleDiscount.secondaryTextKey}`)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1 bg-blue-900 rounded-lg px-3 py-0.5">
                  <div className="flex items-baseline">
                    <span className="text-white text-lg md:text-xl font-extrabold">5%</span>
                    <span className="text-white text-xl md:text-2xl font-extrabold mx-1">+</span>
                    <span className="text-white text-lg md:text-xl font-extrabold">10%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-red-700 text-white text-[0.6rem] md:text-xs font-bold rounded-full px-2 py-0.5">
                      {t('special_discount_banner.departure')}
                    </span>
                    <span className="bg-red-700 text-white text-[0.6rem] md:text-xs font-bold rounded-full px-2 py-0.5">
                      {t('special_discount_banner.return')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

SpecialDiscountBanner.propTypes = {
  variant: PropTypes.oneOf(['etn', 'gho', 'costa', 'gfa', 'rochoa']),
};

SpecialDiscountBanner.defaultProps = {
  variant: 'gho',
};

export default SpecialDiscountBanner;
